import { useMemo } from 'react';
import { Layers, Mic2, Network } from 'lucide-react';
import { Label } from '@/components/ui/label';
import {
  activeAtPlayhead,
  buildSceneMappedTimeline,
} from '@/lib/learningTutorial/sceneMappedTimeline';

function formatTime(sec) {
  const s = Math.max(0, Number(sec) || 0);
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toFixed(0).padStart(2, '0')}`;
}

function PlayheadLine({ playheadPct }) {
  return (
    <div
      className="absolute top-0 bottom-0 w-0.5 bg-amber-400 z-30 pointer-events-none shadow-[0_0_6px_rgba(251,191,36,0.85)]"
      style={{ left: `${playheadPct}%` }}
    />
  );
}

function ClipButton({
  clip,
  isSelected,
  isActive,
  onClick,
  showSceneBadge,
}) {
  return (
    <button
      type="button"
      className={`absolute top-0.5 bottom-0.5 min-w-[3px] rounded border px-0.5 overflow-hidden text-left transition-all hover:brightness-110 z-10 ${clip.colorClass} ${
        isSelected || isActive
          ? 'ring-2 ring-amber-300 ring-offset-1 ring-offset-slate-950 z-20'
          : ''
      }`}
      style={{
        left: `${clip.leftPct}%`,
        width: `${clip.widthPct}%`,
      }}
      title={`${clip.label}${showSceneBadge ? ` · Scene ${clip.sceneIndex + 1}` : ''}\n${clip.narrationPreview || clip.topic || ''}\n${formatTime(clip.start)} – ${formatTime(clip.end)}`}
      onClick={onClick}
    >
      {showSceneBadge && (
        <span className="absolute top-0 left-0 text-[7px] font-bold bg-black/50 px-0.5 rounded-br leading-none">
          S{clip.sceneIndex + 1}
        </span>
      )}
      <span className="block text-[9px] font-semibold truncate leading-tight mt-0.5">
        {clip.label}
      </span>
      <span className="block text-[8px] opacity-80 tabular-nums truncate">
        {formatTime(clip.duration)}
      </span>
    </button>
  );
}

function LaneTrack({
  duration,
  playheadPct,
  onSeek,
  children,
  minHeight = '1.75rem',
}) {
  return (
    <div
      className="relative rounded bg-slate-950/60 border border-slate-800 cursor-pointer"
      style={{ minHeight }}
      onClick={onSeek}
      role="presentation"
    >
      <PlayheadLine playheadPct={playheadPct} />
      {children}
    </div>
  );
}

function LaneSection({
  icon: Icon,
  title,
  laneCount,
  hint,
  children,
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-[10px] text-slate-400">
        <Icon className="w-3.5 h-3.5 shrink-0" />
        <span className="font-medium text-slate-300">{title}</span>
        <span className="text-slate-600">
          {laneCount} lane{laneCount === 1 ? '' : 's'}
          {hint ? ` · ${hint}` : ''}
        </span>
      </div>
      {children}
    </div>
  );
}

/**
 * Scene topic + artifact + audio timelines with minimum non-overlapping lanes.
 */
export function SceneMappedTimeline({
  segments = [],
  duration = 0,
  rendered = null,
  playhead = 0,
  selectedScene = 0,
  selectedBlock = 0,
  onSeek,
  onSelectScene,
  onSelectBlock,
}) {
  const mapped = useMemo(
    () => buildSceneMappedTimeline({ segments, duration, rendered }),
    [segments, duration, rendered],
  );

  const active = useMemo(
    () => activeAtPlayhead(mapped, playhead, rendered?.elements, rendered),
    [mapped, playhead, rendered],
  );

  const playheadPct = duration > 0 ? (playhead / duration) * 100 : 0;

  const handleTrackClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    onSeek?.(ratio * duration);
  };

  const selectAudio = (clip) => {
    onSelectScene?.(clip.sceneIndex);
    const sc = mapped.scenes[clip.sceneIndex];
    const bi = (sc?.audioClips || []).findIndex((b) => b.id === clip.id);
    onSelectBlock?.(clip.sceneIndex, bi >= 0 ? bi : 0);
    onSeek?.(clip.start);
  };

  if (!mapped.scenes.length) {
    return (
      <div className="rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-4 text-xs text-slate-500">
        Generate a timeline to see scene, artifact, and audio mapping.
      </div>
    );
  }

  const audioLaneCount = mapped.audio.laneCount;
  const artifactLaneCount = mapped.artifacts.laneCount;

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900/80 overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-700/80">
        <Label className="text-xs font-medium text-slate-300">
          Scene · artifact · audio mapping
        </Label>
        <span className="text-[10px] text-slate-500 tabular-nums">
          {formatTime(playhead)} / {formatTime(duration)}
        </span>
      </div>

      {/* Ruler */}
      <div className="grid grid-cols-[5.5rem_1fr] gap-2 px-3 mt-2 mb-1">
        <div className="text-[9px] text-slate-600 text-right pr-1 self-center">Time</div>
        <LaneTrack
          duration={duration}
          playheadPct={playheadPct}
          onSeek={handleTrackClick}
          minHeight="1.5rem"
        >
          {[0, 0.25, 0.5, 0.75, 1].map((f) => (
            <div
              key={f}
              className="absolute top-0 bottom-0 w-px bg-slate-700/80"
              style={{ left: `${f * 100}%` }}
            />
          ))}
          {[0, 0.5, 1].map((f) => (
            <span
              key={`lbl-${f}`}
              className="absolute bottom-0 text-[8px] text-slate-600 tabular-nums -translate-x-1/2"
              style={{ left: `${f * 100}%` }}
            >
              {formatTime(f * duration)}
            </span>
          ))}
        </LaneTrack>
      </div>

      <div className="max-h-64 overflow-y-auto overflow-x-hidden px-3 pb-3 space-y-3">
        {/* Scene topic nodes — always 1 lane */}
        <LaneSection
          icon={Network}
          title="Scene topic nodes"
          laneCount={1}
          hint="one row per lesson page"
        >
          <div className="grid grid-cols-[5.5rem_1fr] gap-2">
            <div className="text-[9px] text-slate-500 text-right pr-1 pt-1">Topics</div>
            <LaneTrack playheadPct={playheadPct} onSeek={handleTrackClick}>
              {(mapped.sceneNodes.lanes[0] || []).map((node) => {
                const isActive = active.sceneIndex === node.sceneIndex;
                const isSelected = selectedScene === node.sceneIndex;
                return (
                  <button
                    key={node.id}
                    type="button"
                    className={`absolute top-0.5 bottom-0.5 rounded border px-1 overflow-hidden text-left z-10 bg-indigo-800/70 border-indigo-500/60 text-indigo-50 hover:brightness-110 ${
                      isSelected || isActive ? 'ring-2 ring-amber-300 z-20' : ''
                    }`}
                    style={{
                      left: `${node.leftPct}%`,
                      width: `${node.widthPct}%`,
                    }}
                    title={`${node.label}\n${formatTime(node.start)} – ${formatTime(node.end)}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectScene?.(node.sceneIndex);
                      onSeek?.(node.start);
                    }}
                  >
                    <span className="block text-[10px] font-semibold truncate">
                      {node.sceneIndex + 1}. {node.label}
                    </span>
                  </button>
                );
              })}
            </LaneTrack>
          </div>
        </LaneSection>

        {/* Artifacts — minimum lanes, no overlap on same row */}
        <LaneSection
          icon={Layers}
          title="On-screen artifacts"
          laneCount={mapped.artifacts.laneCount}
          hint="overlapping items split across lanes"
        >
          {artifactLaneCount === 0 ? (
            <p className="text-[10px] text-slate-600 pl-[5.5rem]">No on-screen artifacts in timeline.</p>
          ) : null}
          {Array.from({ length: artifactLaneCount }, (_, lane) => (
            <div key={`art-${lane}`} className="grid grid-cols-[5.5rem_1fr] gap-2">
              <div className="text-[9px] text-slate-500 text-right pr-1 pt-1 tabular-nums">
                L{lane + 1}
              </div>
              <LaneTrack playheadPct={playheadPct} onSeek={handleTrackClick}>
                {(mapped.artifacts.lanes[lane] || []).map((clip) => (
                  <ClipButton
                    key={clip.id}
                    clip={clip}
                    showSceneBadge
                    isSelected={
                      selectedScene === clip.sceneIndex
                      && mapped.scenes[clip.sceneIndex]?.artifactClips?.some(
                        (c, i) => c.id === clip.id && i === selectedBlock,
                      )
                    }
                    isActive={false}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectScene?.(clip.sceneIndex);
                      onSeek?.(clip.start);
                    }}
                  />
                ))}
              </LaneTrack>
            </div>
          ))}
        </LaneSection>

        {/* Audio — minimum lanes */}
        <LaneSection
          icon={Mic2}
          title="Audio narration"
          laneCount={mapped.audio.laneCount}
          hint="mapped to scene blocks in order"
        >
          {audioLaneCount === 0 ? (
            <p className="text-[10px] text-slate-600 pl-[5.5rem]">No narration clips.</p>
          ) : null}
          {Array.from({ length: audioLaneCount }, (_, lane) => (
            <div key={`aud-${lane}`} className="grid grid-cols-[5.5rem_1fr] gap-2">
              <div className="text-[9px] text-slate-500 text-right pr-1 pt-1 tabular-nums">
                L{lane + 1}
              </div>
              <LaneTrack playheadPct={playheadPct} onSeek={handleTrackClick}>
                {(mapped.audio.lanes[lane] || []).map((clip) => {
                  const sc = mapped.scenes[clip.sceneIndex];
                  const bi = (sc?.audioClips || []).findIndex((b) => b.id === clip.id);
                  const isBlockSelected =
                    selectedScene === clip.sceneIndex && selectedBlock === bi;
                  const isBlockActive =
                    active.sceneIndex === clip.sceneIndex && active.blockIndex === bi;
                  return (
                    <ClipButton
                      key={`${clip.id}-audio`}
                      clip={clip}
                      showSceneBadge={mapped.audio.laneCount > 1}
                      isSelected={isBlockSelected}
                      isActive={isBlockActive}
                      onClick={(e) => {
                        e.stopPropagation();
                        selectAudio(clip);
                      }}
                    />
                  );
                })}
              </LaneTrack>
            </div>
          ))}
        </LaneSection>

        {/* Per-scene index (mapping summary) */}
        <div className="border-t border-slate-800 pt-2 space-y-1">
          <div className="text-[10px] font-medium text-slate-400 mb-1">Scene → audio clips</div>
          {mapped.scenes.map((sc) => (
            <div
              key={sc.id}
              className={`flex flex-wrap items-center gap-1 text-[10px] rounded px-2 py-1 ${
                selectedScene === sc.sceneIndex ? 'bg-slate-800/80 ring-1 ring-emerald-500/30' : 'bg-slate-900/50'
              }`}
            >
              <button
                type="button"
                className="font-medium text-slate-200 hover:text-emerald-300 truncate max-w-[8rem]"
                onClick={() => {
                  onSelectScene?.(sc.sceneIndex);
                  onSeek?.(sc.start);
                }}
              >
                {sc.sceneIndex + 1}. {sc.title}
              </button>
              <span className="text-slate-600">→</span>
              {(sc.audioClips || []).map((b, bi) => (
                <button
                  key={b.id}
                  type="button"
                  className={`px-1.5 py-0.5 rounded border text-[9px] ${b.colorClass} ${
                    selectedScene === sc.sceneIndex && selectedBlock === bi
                      ? 'ring-1 ring-amber-300'
                      : ''
                  }`}
                  onClick={() => {
                    onSelectScene?.(sc.sceneIndex);
                    onSelectBlock?.(sc.sceneIndex, bi);
                    onSeek?.(b.start);
                  }}
                >
                  {b.label}
                </button>
              ))}
              {sc.gap && (
                <span className="px-1 py-0.5 rounded border border-dashed border-slate-600 text-slate-500 text-[9px]">
                  gap
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SceneMappedTimeline;
